import * as qs from 'qs';

import type { ApiFailure, ApiRequestActionPayload } from '../types';

function makePathFromParams(map: Map<string, unknown>, separator: string): string {
  if (!(map instanceof Map)) {
    throw new Error('Invalid parameter object. Must be Map');
  }

  let mapString = '';

  map.forEach((value, key) => {
    let strToAdd = '';

    if (value instanceof Map) {
      if (typeof key === 'object') {
        throw new Error(`Invalid parameter key. Must be plain type ${key}`);
      }

      strToAdd += separator + key;
      strToAdd += makePathFromParams(value, separator);
    } else {
      if (typeof value === 'object') {
        throw new Error(`Invalid parameter value. Must be plain type ${key}`);
      }

      strToAdd += separator + value;
    }

    mapString += strToAdd;
  });

  return mapString;
}

async function makeRequest(server: string, url: string, options: RequestInit) {
  options.credentials = 'include';
  options.mode = 'cors';

  const urlToSend = `${server}/${url}`;

  let response: Response;
  let responseString: string;
  try {
    response = await fetch(urlToSend, options);
    responseString = await response.text();
  } catch (error) {
    const aborted =
      options.signal?.aborted ||
      (error !== null &&
        typeof error === 'object' &&
        'name' in error &&
        error.name === 'AbortError');
    throw {
      statusCode: 0,
      code: aborted ? 'ABORTED' : 'NETWORK_ERROR',
    } satisfies ApiFailure;
  }

  let body: unknown;
  try {
    body = responseString === '' ? {} : JSON.parse(responseString);
  } catch {
    if (response.ok) {
      throw { statusCode: response.status, code: 'INVALID_RESPONSE' } satisfies ApiFailure;
    }
  }
  if (response.status >= 200 && response.status < 300) {
    return body;
  }

  const errorBody = body !== null && typeof body === 'object' ? body : {};
  const message = 'message' in errorBody ? errorBody.message : undefined;
  throw {
    statusCode: response.status,
    message:
      typeof message === 'string' ||
      (Array.isArray(message) && message.every((value) => typeof value === 'string'))
        ? message
        : undefined,
    subcode:
      'subcode' in errorBody && typeof errorBody.subcode === 'number'
        ? errorBody.subcode
        : undefined,
  } satisfies ApiFailure;
}

export default function request({
  server,
  controller,
  requestMethod,
  params,
  query,
  body,
  upload,
  accessToken,
}: ApiRequestActionPayload): Promise<unknown> {
  let urlString = controller;
  if (params !== undefined && params) {
    urlString += makePathFromParams(params, '/');
  }

  const queryStr = qs.stringify(query || {});
  urlString += `?${queryStr}`;

  const options: RequestInit = {
    method: requestMethod || 'GET',
  };

  const headers: HeadersInit = {
    Accept: 'application/json',
  };

  if (upload) {
    const form = new FormData();
    for (const [name, value] of Object.entries(upload.fields ?? {})) form.append(name, value);
    form.append('file', upload.file, upload.file.name);
    options.body = form;
    options.signal = upload.signal;
  } else {
    headers['content-type'] = 'application/json';
    if (body) {
      options.body = JSON.stringify(body);
    }
  }

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  options.headers = headers;

  return makeRequest(server, urlString, options);
}
